import React, { useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner } from '@resideo/blueprint-react';
import { useToastContext } from '@resideo/blueprint-oneportal';
import { useQueryClient } from '@tanstack/react-query';
import ScheduleEditor from './recording/components/ScheduleEditor';
import { useTranslation } from 'react-i18next';
import type { RecordingBlock } from './recording/types';
import styles from './ReplaceSchedulePage.module.css';

const ReplaceSchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addToast } = useToastContext();
  const queryClient = useQueryClient();

  const cameraIds = (searchParams.get('cameras') ?? '').split(',').filter(Boolean);
  const count = cameraIds.length;
  const { t } = useTranslation();
  const title = t('bulkEdit.replaceScheduleTitleFallback', {
    count,
    plural: count === 1 ? t('bulkEdit.camera') : t('bulkEdit.cameras'),
  });

  const originalBlocksRef = useRef<RecordingBlock[]>([]);
  const [blocks, setBlocks] = useState<RecordingBlock[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const hasChanges = useMemo(
    () => JSON.stringify(blocks) !== JSON.stringify(originalBlocksRef.current),
    [blocks]
  );

  const handleAddOrEditBlock = (block: RecordingBlock) => {
    setBlocks((prev) => {
      const index = prev.findIndex((b) => b.id === block.id);
      if (index >= 0) {
        const updated = [...prev];
        updated[index] = block;
        return updated;
      }
      return [...prev, block];
    });
  };

  const handleDeleteBlock = (blockId: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== blockId));
  };

  const handleSave = async () => {
    if (cameraIds.length === 0) return;
    setIsSaving(true);
    try {
      originalBlocksRef.current = [...blocks];

      // Invalidate state cache for all cameras so they reflect the new schedule
      cameraIds.forEach((deviceId) => {
        queryClient.invalidateQueries({ queryKey: ['deviceState', deviceId] });
      });

      addToast({
        toastType: 'Success',
        message: t('bulkEdit.saveScheduleSuccess', { count }),
      });

      // Pass blocks back via navigation state so BulkEditSettings can include
      // them in the bulk apply operation without sessionStorage.
      const cameras = searchParams.get('cameras') ?? '';
      navigate(`/camera/bulk-edit?cameras=${encodeURIComponent(cameras)}`, { state: { scheduleBlocks: blocks } });
    } catch (error: any) {
      addToast({
        toastType: 'Error',
        message: error?.message || t('bulkEdit.saveScheduleError'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => navigate(-1);
  const handleCancel = () => navigate(-1);

  return (
    <div className={styles.pageContainer}>
      {isSaving ? (
        <div className={styles.loadingContainer}>
          <Spinner size="large" />
        </div>
      ) : (
        <ScheduleEditor
          cameraName={title}
          blocks={blocks}
          onBack={handleBack}
          onSave={handleSave}
          onCancel={handleCancel}
          hasChanges={hasChanges}
          isSaving={isSaving}
          onAddBlock={handleAddOrEditBlock}
          onEditBlock={handleAddOrEditBlock}
          onDeleteBlock={handleDeleteBlock}
          disabled={isSaving}
        />
      )}
    </div>
  );
};

export default ReplaceSchedulePage;
